import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthShell } from '@/components/auth/AuthShell';
import { AuthField } from '@/components/auth/AuthField';
import { NeonButton } from '@/components/ui/NeonButton';
import { Icon } from '@/components/ui/Icon';
import { useAuth } from '@/modules/auth/useAuth';
import { ROUTES } from '@/config/routes';

/**
 * Password recovery — step 1. Sends a Supabase recovery email; the link
 * lands on `/auth/reset`. Mock mode pretends to send and uses a localStorage
 * hint so the flow still works offline.
 */
export const ForgotPasswordPage = () => {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await requestPasswordReset(email);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Could not send recovery email.');
      return;
    }
    setSent(true);
  };

  return (
    <AuthShell
      eyebrow="Reset access"
      title={
        <>
          Forgot the room key<span className="text-pink">.</span>
        </>
      }
      intro={
        <>
          We&rsquo;ll send a link that lets you set a new password. No code,
          no fuss<span className="text-pink">.</span>
        </>
      }
      footer={
        <p className="text-center font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          Remembered it?{' '}
          <Link to={ROUTES.landing} className="text-pink hover:underline">
            Log in
          </Link>
        </p>
      }
    >
      {sent ? (
        <div className="flex flex-col items-start gap-4">
          <p className="font-display text-sm font-bold uppercase tracking-wide text-text-primary">
            Check your inbox<span className="text-pink">.</span>
          </p>
          <p className="text-xs text-text-secondary">
            If an account exists for{' '}
            <span className="text-text-primary">{email}</span>, a recovery
            link is on the way. The link expires in 1 hour.
          </p>
          <Link to={ROUTES.landing}>
            <NeonButton variant="outline" size="md" leading={<Icon name="arrow_back" size={14} />}>
              Back to landing
            </NeonButton>
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <AuthField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
          />
          {error && (
            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
              — {error}
            </p>
          )}
          <NeonButton
            type="submit"
            size="md"
            disabled={submitting || !email.trim()}
            trailing={<Icon name="arrow_forward" size={14} />}
          >
            {submitting ? 'Sending' : 'Send recovery link'}
          </NeonButton>
        </form>
      )}
    </AuthShell>
  );
};
